/**
 * Admin API call functions.
 * All requests hit /api/admin/* endpoints and require admin JWT.
 */

import apiClient from './client';
import type { Question } from '../types';
import type {
  AdminDashboard,
  AdminFlag,
  AdminFlagPatch,
  AdminFlagsResponse,
  AdminNotificationList,
  AdminQuestionCreatePayload,
  AdminQuestionDetail,
  AdminQuestionItem,
  AdminQuestionList,
  AdminQuestionUpdatePayload,
  AdminQuestionsParams,
  AdminQuestionsStats,
  AdminSessionList,
  AdminSessionsParams,
  AdminSettingPatch,
  AdminSettings,
  AdminSettingsResponse,
  AdminUserDetail,
  AdminUserList,
  AdminUsersParams,
  AnalyticsOverview,
  AuditLogList,
  AuditLogParams,
  CalibrationApplyResponse,
  CalibrationData,
  CalibrationUpdate,
  CoverageData,
  ImportResult,
  ProblematicQuestionsList,
  ProblemsParams,
  PurgeCdnResponse,
  QuestionImageUploadResult,
  QuestionReport,
  QuestionReportList,
  RefreshStatsResult,
  DeviceSessionList,
  EngagementOverview,
  ResetPasswordResponse,
  SessionAnalytics,
  TopicHealthList,
  UserDashboardData,
} from '../types/admin';

/* ── Dashboard ─────────────────────────────────────────────────────────────── */

/** Fetch aggregate KPIs for the admin dashboard, optionally filtered by certification. */
export async function fetchAdminDashboard(certification?: string): Promise<AdminDashboard> {
  const params = certification ? { certification } : undefined;
  const { data } = await apiClient.get<AdminDashboard>('/api/admin/dashboard', { params });
  return data;
}

/* ── User Management ───────────────────────────────────────────────────────── */

/** Fetch paginated admin user list with optional filters. */
export async function fetchAdminUsers(params?: AdminUsersParams): Promise<AdminUserList> {
  const { data } = await apiClient.get<AdminUserList>('/api/admin/users', { params });
  return data;
}

/** Fetch detailed information for a single user. */
export async function fetchAdminUserDetail(userId: number): Promise<AdminUserDetail> {
  const { data } = await apiClient.get<AdminUserDetail>(`/api/admin/users/${userId}`);
  return data;
}

/** Update a user's active status. */
export async function updateUserStatus(userId: number, isActive: boolean): Promise<AdminUserDetail> {
  const { data } = await apiClient.patch<AdminUserDetail>(
    `/api/admin/users/${userId}/status`,
    { is_active: isActive },
  );
  return data;
}

/** Update a user's admin role. */
export async function updateUserRole(userId: number, isAdmin: boolean): Promise<AdminUserDetail> {
  const { data } = await apiClient.patch<AdminUserDetail>(
    `/api/admin/users/${userId}/role`,
    { is_admin: isAdmin },
  );
  return data;
}

/** Soft-delete a user account. */
export async function deleteUser(userId: number): Promise<void> {
  await apiClient.delete(`/api/admin/users/${userId}`);
}

/** Change a user's plan (e.g. downgrade to free). */
export async function updateUserPlan(userId: number, plan: string): Promise<{ id: number; username: string; plan: string }> {
  const { data } = await apiClient.patch(`/api/admin/users/${userId}/plan`, { plan });
  return data;
}

/** Reset a user's password and return the temporary password. */
export async function resetUserPassword(userId: number): Promise<ResetPasswordResponse> {
  const { data } = await apiClient.post<ResetPasswordResponse>(
    `/api/admin/users/${userId}/reset-password`,
  );
  return data;
}

/* ── Question Management ───────────────────────────────────────────────────── */

/** Fetch paginated admin question list with filters and sorting. */
export async function fetchAdminQuestions(params?: AdminQuestionsParams): Promise<AdminQuestionList> {
  const { data } = await apiClient.get<AdminQuestionList>('/api/admin/questions', { params });
  return data;
}

/** Fetch a single question by ID (admin view). */
export async function fetchAdminQuestion(id: number): Promise<AdminQuestionDetail> {
  const { data } = await apiClient.get<AdminQuestionDetail>(`/api/admin/questions/${id}`);
  return data;
}

/** Create a new question. */
export async function createQuestion(payload: AdminQuestionCreatePayload): Promise<Question> {
  const { data } = await apiClient.post<Question>('/api/admin/questions', payload);
  return data;
}

/** Update an existing question. */
export async function updateQuestion(id: number, payload: AdminQuestionUpdatePayload): Promise<Question> {
  const { data } = await apiClient.put<Question>(`/api/admin/questions/${id}`, payload);
  return data;
}

/** Delete a question by ID. */
export async function deleteQuestion(id: number): Promise<void> {
  await apiClient.delete(`/api/admin/questions/${id}`);
}

/** Toggle a question's active flag (admin soft-disable). */
export async function toggleQuestionActive(
  id: number,
  isActive: boolean,
): Promise<AdminQuestionItem> {
  const { data } = await apiClient.patch<AdminQuestionItem>(
    `/api/admin/questions/${id}/active`,
    { is_active: isActive },
  );
  return data;
}

/** Upload an image for a question. When `append` is true the file is added
 *  to the `images[]` gallery; otherwise it replaces the main `image_path`. */
export async function uploadQuestionImage(
  id: number,
  file: File,
  append = false,
): Promise<QuestionImageUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<QuestionImageUploadResult>(
    `/api/admin/questions/${id}/image`,
    form,
    {
      params: { append },
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  );
  return data;
}

/** Remove all images attached to a question. */
export async function deleteQuestionImage(id: number): Promise<void> {
  await apiClient.delete(`/api/admin/questions/${id}/image`);
}

/** Import questions from a JSON file upload. */
export async function importQuestions(file: File): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ImportResult>('/api/admin/questions/import-json', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Fetch corpus statistics for the admin question management view. */
export async function fetchQuestionStats(): Promise<AdminQuestionsStats> {
  const { data } = await apiClient.get<AdminQuestionsStats>('/api/admin/questions/stats');
  return data;
}

/* ── Session Management ────────────────────────────────────────────────────── */

/** Fetch paginated admin session list with optional filters. */
export async function fetchAdminSessions(params?: AdminSessionsParams): Promise<AdminSessionList> {
  const { data } = await apiClient.get<AdminSessionList>('/api/admin/sessions', { params });
  return data;
}

/** Fetch global session analytics. */
export async function fetchSessionAnalytics(): Promise<SessionAnalytics> {
  const { data } = await apiClient.get<SessionAnalytics>('/api/admin/sessions/analytics');
  return data;
}

/* ── Question Analytics ───────────────────────────────────────────────────── */

export async function fetchAnalyticsOverview(): Promise<AnalyticsOverview> {
  const { data } = await apiClient.get<AnalyticsOverview>('/api/admin/analytics/overview');
  return data;
}

export async function fetchTopicHealth(): Promise<TopicHealthList> {
  const { data } = await apiClient.get<TopicHealthList>('/api/admin/analytics/topics');
  return data;
}

export async function fetchProblematicQuestions(params?: ProblemsParams): Promise<ProblematicQuestionsList> {
  const { data } = await apiClient.get<ProblematicQuestionsList>('/api/admin/analytics/problems', { params });
  return data;
}

export async function fetchCalibration(minAttempts?: number, limit?: number): Promise<CalibrationData> {
  const { data } = await apiClient.get<CalibrationData>('/api/admin/analytics/calibration', {
    params: { min_attempts: minAttempts, limit },
  });
  return data;
}

export async function applyCalibration(updates: CalibrationUpdate[]): Promise<CalibrationApplyResponse> {
  const { data } = await apiClient.patch<CalibrationApplyResponse>('/api/admin/analytics/calibration/apply', { updates });
  return data;
}

export async function fetchCoverage(targetPerTopic?: number): Promise<CoverageData> {
  const { data } = await apiClient.get<CoverageData>('/api/admin/analytics/coverage', {
    params: { target_per_topic: targetPerTopic },
  });
  return data;
}

export async function refreshAnalyticsCache(): Promise<RefreshStatsResult> {
  const { data } = await apiClient.post<RefreshStatsResult>('/api/admin/analytics/refresh');
  return data;
}

/* ── User Dashboard ───────────────────────────────────────────────────────── */

export async function fetchUserDashboard(certification?: string): Promise<UserDashboardData> {
  const { data } = await apiClient.get<UserDashboardData>('/api/auth/me/dashboard', {
    params: certification ? { certification } : undefined,
  });
  return data;
}

/** Fetch full dashboard data for any user (admin only). */
export async function fetchAdminUserDashboard(userId: number): Promise<UserDashboardData> {
  const { data } = await apiClient.get<UserDashboardData>(`/api/admin/users/${userId}/dashboard`);
  return data;
}

/* ── Admin User Stats Dashboard ────────────────────────────────────────────── */

import type { AdminUserStatsDashboard } from '../types/admin';

export async function fetchAdminUserStats(): Promise<AdminUserStatsDashboard> {
  const { data } = await apiClient.get<AdminUserStatsDashboard>('/api/admin/user-stats');
  return data;
}

/* ── Audit Log ────────────────────────────────────────────────────────────── */

/** Fetch paginated audit log entries with optional filters. */
export async function fetchAuditLog(params?: AuditLogParams): Promise<AuditLogList> {
  const { data } = await apiClient.get<AuditLogList>('/api/admin/audit-log', { params });
  return data;
}

/* ── Question Reports ─────────────────────────────────────────────────────── */

/** Report a question as problematic (user-facing). */
export async function reportQuestion(
  questionId: number,
  payload: { reason_type: string; description: string },
): Promise<{ id: number; message: string }> {
  const { data } = await apiClient.post<{ id: number; message: string }>(
    `/api/questions/${questionId}/report`,
    payload,
  );
  return data;
}

/** Fetch question reports (admin). */
export async function fetchReports(params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<QuestionReportList> {
  const { data } = await apiClient.get<QuestionReportList>('/api/admin/reports', { params });
  return data;
}

/** Resolve or dismiss a report (admin). */
export async function resolveReport(
  reportId: number,
  payload: { status: string; admin_note: string },
): Promise<QuestionReport> {
  const { data } = await apiClient.patch<QuestionReport>(`/api/admin/reports/${reportId}`, payload);
  return data;
}

/* ── Admin Notifications ──────────────────────────────────────────────────── */

/** Fetch admin notifications. */
export async function fetchNotifications(params?: {
  is_read?: boolean;
  limit?: number;
  offset?: number;
}): Promise<AdminNotificationList> {
  const { data } = await apiClient.get<AdminNotificationList>('/api/admin/notifications', { params });
  return data;
}

/** Mark a single notification as read. */
export async function markNotificationRead(id: number): Promise<void> {
  await apiClient.patch(`/api/admin/notifications/${id}/read`);
}

/** Mark all notifications as read. */
export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.patch('/api/admin/notifications/read-all');
}

/** Delete a notification. */
export async function deleteNotification(id: number): Promise<void> {
  await apiClient.delete(`/api/admin/notifications/${id}`);
}

/* ── Settings ──────────────────────────────────────────────────────────────── */

/** Fetch the full platform settings map. */
export async function fetchAdminSettings(): Promise<AdminSettings> {
  const { data } = await apiClient.get<AdminSettingsResponse>('/api/admin/settings');
  return data.settings;
}

/** Patch a single setting key. Returns the updated settings map. */
export async function patchAdminSetting(payload: AdminSettingPatch): Promise<AdminSettings> {
  const { data } = await apiClient.patch<AdminSettingsResponse>('/api/admin/settings', payload);
  return data.settings;
}

/* ── Feature Flags ──────────────────────────────────────────────────────────── */

/** Fetch all feature flags. */
export async function fetchAdminFlags(): Promise<AdminFlag[]> {
  const { data } = await apiClient.get<AdminFlagsResponse>('/api/admin/flags');
  return data.items;
}

/** Update enabled state and/or rollout percent for a single flag. */
export async function patchAdminFlag(key: string, payload: AdminFlagPatch): Promise<AdminFlag> {
  const { data } = await apiClient.patch<AdminFlag>(`/api/admin/flags/${key}`, payload);
  return data;
}

/* ── Ops ───────────────────────────────────────────────────────────────────── */

/** Purge CDN cache. Resolves with {purged:true} on 200; rejects with AxiosError on 501/502. */
export async function purgeCdn(): Promise<PurgeCdnResponse> {
  const { data } = await apiClient.post<PurgeCdnResponse>('/api/admin/ops/purge-cdn');
  return data;
}

/** Trigger a full platform data export. Returns the file blob for immediate download. */
export async function exportPlatformData(): Promise<Blob> {
  const { data } = await apiClient.post('/api/admin/ops/export', null, {
    responseType: 'blob',
  });
  return data as Blob;
}

/* ── Access · RBAC ────────────────────────────────────────────────────────── */

export interface AdminRbacRole {
  key: 'owner' | 'admin' | 'editor' | 'support';
  count: number;
}

export interface AdminRbacPermission {
  permission: string;
  grants: Record<AdminRbacRole['key'], boolean>;
}

export interface AdminRbacResponse {
  roles: AdminRbacRole[];
  items: AdminRbacPermission[];
}

/** Fetch the RBAC permission matrix (role counts + grants). */
export async function fetchAdminRbac(): Promise<AdminRbacResponse> {
  const { data } = await apiClient.get<AdminRbacResponse>('/api/admin/rbac');
  return data;
}

/** Toggle a single permission grant for a role. Owner role returns 403. */
export async function patchAdminRbac(payload: {
  role: AdminRbacRole['key'];
  permission: string;
  allowed: boolean;
}): Promise<void> {
  await apiClient.patch('/api/admin/rbac', payload);
}

/* ── Access · B2B Licenses ────────────────────────────────────────────────── */

export type AdminLicenseStatus = 'active' | 'full' | 'expiring';

export interface AdminLicense {
  id: string;
  org_id: string;
  org_name: string;
  access_code: string;
  seats_total: number;
  seats_used: number;
  expires_at: string | null;
  created_at: string;
  status: AdminLicenseStatus;
}

export interface AdminLicenseList {
  items: AdminLicense[];
}

export interface AdminLicenseCreatePayload {
  org_name: string;
  seats_total: number;
  expires_at: string | null;
}

export interface AdminLicensePatchPayload {
  seats_total?: number;
  seats_used?: number;
  expires_at?: string | null;
}

/** List all B2B organization licenses. */
export async function fetchAdminLicenses(): Promise<AdminLicenseList> {
  const { data } = await apiClient.get<AdminLicenseList>('/api/admin/licenses');
  return data;
}

/** Create a new B2B license (server generates the access code). */
export async function createAdminLicense(
  payload: AdminLicenseCreatePayload,
): Promise<AdminLicense> {
  const { data } = await apiClient.post<AdminLicense>('/api/admin/licenses', payload);
  return data;
}

/** Update seats or expiry for an existing license. */
export async function patchAdminLicense(
  id: string,
  payload: AdminLicensePatchPayload,
): Promise<AdminLicense> {
  const { data } = await apiClient.patch<AdminLicense>(`/api/admin/licenses/${id}`, payload);
  return data;
}

/** Delete a license by id. */
export async function deleteAdminLicense(id: string): Promise<void> {
  await apiClient.delete(`/api/admin/licenses/${id}`);
}

/* ── Access · Email Domains ───────────────────────────────────────────────── */

export interface AdminEmailDomainItem {
  domain: string;
  created_at: string;
}

export interface AdminEmailDomainsResponse {
  restrict: boolean;
  items: AdminEmailDomainItem[];
}

/** Fetch the email-domain allowlist and restrict switch state. */
export async function fetchAdminEmailDomains(): Promise<AdminEmailDomainsResponse> {
  const { data } = await apiClient.get<AdminEmailDomainsResponse>('/api/admin/email-domains');
  return data;
}

/** Add a domain to the allowlist. Returns 409 if it already exists. */
export async function addAdminEmailDomain(domain: string): Promise<AdminEmailDomainItem> {
  const { data } = await apiClient.post<AdminEmailDomainItem>('/api/admin/email-domains', {
    domain,
  });
  return data;
}

/** Remove a domain from the allowlist. */
export async function deleteAdminEmailDomain(domain: string): Promise<void> {
  await apiClient.delete(`/api/admin/email-domains/${domain}`);
}

/** Persist the restrict-signup-domains switch via the settings endpoint. */
export async function patchAdminRestrictDomains(on: boolean): Promise<void> {
  await apiClient.patch('/api/admin/settings', {
    key: 'restrict_signup_domains',
    value: { on },
  });
}

/* ── Activity / Engagement ─────────────────────────────────────────────────── */

/** Fetch engagement KPIs + DAU series + feature usage for the Traffic tab.
 *  @param days  Window in days — 7, 30, or 90 (default 30). */
export async function fetchEngagementOverview(days = 30): Promise<EngagementOverview> {
  const { data } = await apiClient.get<EngagementOverview>('/api/admin/activity/engagement', {
    params: { days },
  });
  return data;
}

/** Fetch global device sessions (no user filter).
 *  @param limit  Max rows to return (default 50). */
export async function fetchDeviceSessions(limit = 50): Promise<DeviceSessionList> {
  const { data } = await apiClient.get<DeviceSessionList>('/api/admin/activity/devices', {
    params: { limit },
  });
  return data;
}

/** Fetch device sessions for a specific user (used in the drawer). */
export async function fetchUserDeviceSessions(userId: number, limit = 50): Promise<DeviceSessionList> {
  const { data } = await apiClient.get<DeviceSessionList>('/api/admin/activity/devices', {
    params: { user_id: userId, limit },
  });
  return data;
}

/** Revoke a device session by its ID.  Returns 204 on success. */
export async function revokeDeviceSession(sessionId: number): Promise<void> {
  await apiClient.delete(`/api/admin/activity/devices/${sessionId}`);
}
